// Brand Ambassador – App root
// ─────────────────────────────────────
// • Javna stranica: src/components/PublicApp.js
// • Dashboard:      src/dashboard/  (moduli se učitavaju lijeno)
// • Autentikacija:  PHP backend (/api/auth.php) — lozinke nisu u sourceu
// ─────────────────────────────────────

import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import "./App.css";
import "./styles/public.css";
import "./styles/dashboard.css";

import { Ctx } from "./dashboard/context";
import { T } from "./dashboard/i18n";
import { ThemeProvider } from "./dashboard/theme";
import { ToastProvider, useToast } from "./dashboard/ui/Toast";
import Login from "./dashboard/Login";

// Javna stranica i dashboard su dva odvojena bundlea — učita se samo onaj koji treba.
const PublicApp = lazy(() => import("./components/PublicApp"));
const AdminDash = lazy(() => import("./dashboard/AdminDash"));

const ICONS_CDN = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css";
const FONTS_CDN = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300&family=DM+Sans:wght@300;400;500;600&display=swap";

/** Automatska odjava nakon neaktivnosti — s upozorenjem prije isteka. */
const IDLE_MS    = 20 * 60 * 1000;
const WARN_AHEAD =  2 * 60 * 1000;

function useStylesheet(id, href) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id; l.rel = "stylesheet"; l.href = href;
    document.head.appendChild(l);
  }, [id, href]);
}

function AppInner() {
  const { toast } = useToast();
  const [lang, setLang] = useState("hr");
  const [page, setPage] = useState("home");
  const [showLogin, setShowLogin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const isAdmin = !!currentUser;

  useStylesheet("ba-icons", ICONS_CDN);
  useStylesheet("ba-fonts", FONTS_CDN);
  useEffect(() => { document.title = "Brand Ambassador"; }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem("ba_token");
    sessionStorage.removeItem("ba_user");
    setCurrentUser(null);
  }, []);

  // Token se provjerava na serveru — jedini način dobiti isAdmin je validan JWT
  useEffect(() => {
    const token = sessionStorage.getItem("ba_token");
    if (!token) { sessionStorage.removeItem("ba_user"); return; }
    fetch("/api/verify.php", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data?.valid) setCurrentUser(data.role); else logout(); })
      .catch(() => {}); // lokalni dev bez backenda
  }, [logout]);

  const t = useCallback(key => {
    let v = T[lang];
    for (const p of key.split(".")) v = v?.[p];
    return v ?? key;
  }, [lang]);

  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch("/api/auth.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      sessionStorage.setItem("ba_token", data.token);
      sessionStorage.setItem("ba_user", data.role);
      setCurrentUser(data.role);
      setShowLogin(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  // ── Auto-odjava zbog neaktivnosti (s upozorenjem) ──
  const timers = useRef({});
  useEffect(() => {
    if (!isAdmin) return;
    const clear = () => { clearTimeout(timers.current.warn); clearTimeout(timers.current.out); };
    const arm = () => {
      clear();
      timers.current.warn = setTimeout(
        () => toast("Zbog neaktivnosti odjava slijedi za 2 minute.", "err", 12000),
        IDLE_MS - WARN_AHEAD
      );
      timers.current.out = setTimeout(logout, IDLE_MS);
    };
    arm();
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "wheel"];
    events.forEach(e => window.addEventListener(e, arm, { passive: true }));
    return () => { clear(); events.forEach(e => window.removeEventListener(e, arm)); };
  }, [isAdmin, logout, toast]);

  const ctx = useMemo(() => ({
    lang, setLang, t, page, setPage, showLogin, setShowLogin,
    isAdmin, currentUser, login, logout,
    setIsAdmin: v => { if (!v) logout(); },
  }), [lang, t, page, showLogin, isAdmin, currentUser, login, logout]);

  return (
    <Ctx.Provider value={ctx}>
      <Suspense fallback={<div className="app-boot"><span className="app-boot-mark">BA</span></div>}>
        {isAdmin ? <AdminDash /> : <PublicApp onAdminOpen={() => setShowLogin(true)} />}
      </Suspense>
      {showLogin && !isAdmin && <Login />}
    </Ctx.Provider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AppInner />
      </ToastProvider>
    </ThemeProvider>
  );
}
